import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { useProjectModal } from 'components/Modal/Project/Project';
import * as actions from 'containers/App/actions';
import { centsToDollars } from 'utils/general';
import dayjs from 'dayjs';
import { useAddNewLead } from '../../../Leads/CreateLeadForm';
import './stats-widget.scss';


const StatsWidget = ({ projectsLimit, filters, tasksInfo, financialInfo, userInfo, currency }) => {
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [recentTasks, setRecentTasks] = useState(0);
  const [projectModal, setProjectModalOpen] = useProjectModal();
  const [createNewLeadModal, setNewLeadModalOpen] = useAddNewLead();
  const history = useHistory();
  const today = dayjs();
  const { effectivePermissions, permissions } = userInfo;
  const dispatch = useDispatch();
  const { setNotificationsCenterOpen, setNotificationsCenterTab } = bindActionCreators(actions, dispatch);
  const notificationsOpen = useSelector(state => state.global.notificationCenterOpen);

  const hasProTools = userInfo?.features?.includes('PRO-PLANNING');
  const isBasicAccount = !hasProTools;

  const showSalesTools = () => {
    return (hasProTools && permissions?.['lead.all']) || effectivePermissions?.is_bookkeeper;
  };

  const toggleNotificationsCenter = () => {
    setNotificationsCenterTab('allMyTasks');
    setNotificationsCenterOpen(!notificationsOpen);
  };

  const leadsCount = useMemo(() => {
    if (filters && filters.length) {
      return filters
        .filter(item => item.current)
        .reduce((sum, status) => sum + status.count, 0);
    }
    return 0;
  }, [filters]);

  const newLeadsCount = useMemo(() => {
    if (filters && filters.length) {
      return filters
        .filter(item => item.name === 'New')
        .reduce((acc, status) => ({ sum: acc.sum + status.count, newId: status.id }), { newId: '', sum: 0 });
    }
    return { newId: '', sum: 0 };
  }, [filters]);

  useEffect(() => {
    let tempTasks = [];
    if (
      tasksInfo?.assignedTasks &&
      tasksInfo?.leads &&
      tasksInfo?.loginTasks &&
      tasksInfo?.weddings
    ) {
      tempTasks = [
        ...tasksInfo.assignedTasks,
        ...tasksInfo.leads.map(l => l.tasks).flat(),
        ...tasksInfo.loginTasks.tasks,
        ...tasksInfo.weddings.map(w => w.tasks).flat(),
      ].filter(t => t.dueDate && dayjs(t.dueDate).isBefore(today));
      // Dedupe - assignedTasks can overlap with the other categories
      tempTasks = [...new Map(tempTasks.map(t => [t.id, t])).values()];
      setOverdueTasks(tempTasks);
    }

    // Set recent tasks count - tasks due in the last 3 days
    setRecentTasks(
      tempTasks
        .filter((t) => dayjs(t.dueDate).isBefore(today) && dayjs(t.dueDate).isAfter(today.subtract(3, 'day')))
        .length,
    );
  }, [tasksInfo]);

  const revenueDisplayAmount = centsToDollars(financialInfo?.paid_amount || 0, currency);

  function handleLeadClick(e) {
    e.stopPropagation();
    history.push(`/app/v2/${userInfo.orgId}/leads/status/current`);
  }

  function handleLeadChildClick(e) {
    e.stopPropagation();
    history.push(`/app/v2/${userInfo.orgId}/leads/status/${newLeadsCount.newId}`);
  }

  function handleTaskChildClick(e) {
    e.stopPropagation();
    // Somewhat redundant
    // But maintaining pattern
    // in case requirements change
    toggleNotificationsCenter();
  }

  function handleAddProject(e) {
    e.stopPropagation();
    if (isBasicAccount) {
      setProjectModalOpen('upgrade');
    } else if (projectsLimit?.isExceeded) {
      setProjectModalOpen('limit');
    } else {
      setProjectModalOpen('project');
    }
  };

  function handleAddLead(e) {
    e.stopPropagation();
    setNewLeadModalOpen(true);
  }

  function handleAddTask(e) {
    e.stopPropagation();
    // Somewhat redundant
    // But maintaining pattern
    // in case requirements change
    toggleNotificationsCenter();
  }

  function handleAddInvoice(e) {
    e.stopPropagation();
    // A little redundant here as well
    // but we don't actually have a nice modal for this
    // and the one we do have is wrapped up in angular
    window.location.href = `/app/pro/${userInfo?.orgId}/invoices`;
  }

  function handleProjectClick(e) {
    e.stopPropagation();
    history.push('/app/v2/projects');
  }

  function PlusSign() {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8 4v8M4 8h8"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <>
      {createNewLeadModal}
      {projectModal}
      <div className="stats-grid">
        {!effectivePermissions?.is_bookkeeper && (
          <div
            className="stats-grid__item"
            onClick={handleProjectClick}
          >
            <div className="stats-grid__item-primary">
              <div className="stats-grid__item-leading">
                <div className="stats-grid__item-empty" />
              </div>
              <div className="stats-grid__item-value">{projectsLimit?.activeWeddingCount || 0}</div>
              <div className="stats-grid__item-label">Active Projects</div>
            </div>
            <button
              type="button"
              className="stats-grid__item-after"
              onClick={handleAddProject}
            >
              <PlusSign />
            </button>
          </div>
        )}
        {(effectivePermissions?.['lead.all'] || effectivePermissions?.['lead.full-access']) && (
          <div
            className="stats-grid__item"
            onClick={handleLeadClick}
          >
            <div className="stats-grid__item-primary">
              <div className="stats-grid__item-leading">
                {newLeadsCount.sum > 0 ? (
                  <button
                    type="button"
                    className="stats-grid__item-before"
                    onClick={handleLeadChildClick}
                  >
                    {newLeadsCount.sum}
                  </button>
                ) : <div className="stats-grid__item-empty" />}
              </div>
              <div className="stats-grid__item-value">{leadsCount || 0}</div>
              <div className="stats-grid__item-label">Current Leads</div>
            </div>
            <button
              type="button"
              className="stats-grid__item-after"
              onClick={handleAddLead}
            >
              <PlusSign />
            </button>
          </div>
        )}
        <div
          className="stats-grid__item"
          onClick={toggleNotificationsCenter}
        >
          <div className="stats-grid__item-primary">
            <div className="stats-grid__item-leading">
              {recentTasks > 0 ? (
                <button
                  type="button"
                  className="stats-grid__item-before"
                  onClick={handleTaskChildClick}
                >
                  {recentTasks}
                </button>
              ) : <div className="stats-grid__item-empty" />}
            </div>
            <div className="stats-grid__item-value">{overdueTasks.length}</div>
            <div className="stats-grid__item-label">Overdue Tasks</div>
          </div>
          <button
            type="button"
            className="stats-grid__item-after"
            onClick={handleAddTask}
          >
            <PlusSign />
          </button>
        </div>
        {showSalesTools() && (
          <div
            className="stats-grid__item"
            onClick={() => { window.location.href = `/app/pro/${userInfo.orgId}/invoices`; }}
          >
            <div className="stats-grid__item-primary">
              <div className="stats-grid__item-leading">
                <div className="stats-grid__item-empty" />
              </div>
              <div className="stats-grid__item-value">{revenueDisplayAmount}</div>
              <div className="stats-grid__item-label">
                YTD Revenue
                {currency && <span className="stats-grid__item-label__currency"> per {currency}</span>}
              </div>
            </div>
            <button
              type="button"
              className="stats-grid__item-after"
              onClick={handleAddInvoice}
            >
              <PlusSign />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default StatsWidget;
